/**
 * Type definitions for the Customer Log Experience
 * These types align with the backend audit tracing API (user story #7036)
 */

/**
 * Log severity levels - standardized across the application
 */
export type LogLevel = 'Info' | 'Success' | 'Warning' | 'Error';

/**
 * Migration stages that can generate log entries
 */
export type MigrationStage = 'Export' | 'Transform' | 'Import';

/**
 * Individual log entry from the backend
 */
export interface MigrationLogEntry {
  /** Unique identifier for this log entry */
  id: string;

  /** ID of the migration this log belongs to */
  migrationId: string;

  /** ISO 8601 timestamp of when the event occurred */
  timestamp: string;

  /** Severity level of the log entry */
  level: LogLevel;

  /** Which stage of migration this log is from */
  stage: MigrationStage;

  /** Customer-friendly message (pre-transformed by backend) */
  message: string;

  /** Type of resource this log relates to (e.g., "Queue", "User") */
  resourceType?: string;

  /** Specific resource name (e.g., "Support_Queue_Main") */
  resourceName?: string;

  /** Additional structured metadata about the event */
  details?: Record<string, unknown>;

  /** Optional guidance for the user on how to respond to this log */
  recommendedAction?: string;

  /** Link to relevant documentation or help resources */
  documentationUrl?: string;
}

/**
 * API response when fetching logs for a migration
 */
export interface MigrationLogsResponse {
  /** ID of the migration */
  migrationId: string;

  /** Display name of the migration */
  migrationName: string;

  /** Total number of log entries (across all pages) */
  totalCount: number;

  /** Array of log entries for the current page */
  logs: MigrationLogEntry[];
}

/**
 * Frontend state for log filtering and sorting
 */
export interface LogFilters {
  /** Selected stages to display (empty = all stages) */
  stages: MigrationStage[];

  /** Selected log levels to display (empty = all levels) */
  levels: LogLevel[];

  /** Free-text search term */
  searchTerm: string;

  /** Field to sort by */
  sortBy: 'timestamp';

  /** Sort direction */
  sortDirection: 'asc' | 'desc';

  /** Current page number (1-based) */
  page: number;

  /** Number of items per page */
  pageSize: number;
}

/**
 * Default filter state
 */
export const DEFAULT_LOG_FILTERS: LogFilters = {
  stages: [],
  levels: [],
  searchTerm: '',
  sortBy: 'timestamp',
  sortDirection: 'desc',
  page: 1,
  pageSize: 10
};
